import React, { useEffect, useState } from "react";
import { AdminOffer, CandidateRow } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  DetailRow,
  Empty,
  Section,
  Table,
  SortConfig,
  SortDirection,
  sortData,
} from "../AdminUI";

interface Props {
  offer: AdminOffer;
  onSelectCandidate: (row: CandidateRow) => void;
  cachedRows: CandidateRow[] | undefined;
  onCache: (offerId: string, data: CandidateRow[]) => void;
  refreshKey: number;
}

export default function OfferDetails({
  offer,
  onSelectCandidate,
  cachedRows,
  onCache,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CandidateRow[]>(cachedRows || []);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedRows = React.useMemo(() => {
    return sortData(rows, sortConfig);
  }, [rows, sortConfig]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (cachedRows && refreshKey === 0) {
          setRows(cachedRows);
          setLoading(false);
          return;
        }
        setLoading(true);
        const data = await adminService.fetchOfferCandidates(offer.id);
        if (!mounted) return;
        setRows(data || []);
        onCache(offer.id, data || []);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [offer.id, refreshKey]);

  const cols: Column<CandidateRow>[] = [
    { key: "first_name", header: "Nombre", sortable: true },
    { key: "last_name", header: "Apellido", sortable: true },
    { key: "email", header: "Email", sortable: true },
    { key: "phone", header: "Teléfono", sortable: true },
    { key: "nif", header: "NIF", sortable: true },
    {
      key: "status",
      header: "Estado",
      render: (r) => {
        const s = (r.status || "").toLowerCase();
        let badgeClass = "status-draft";
        if (
          s === "active" ||
          s === "hired" ||
          s === "accepted" ||
          s === "finalist"
        ) {
          badgeClass = "status-active";
        } else if (s === "rejected" || s === "discarded") {
          badgeClass = "status-closed";
        }
        return <span className={`badge ${badgeClass}`}>{r.status || "—"}</span>;
      },
      sortable: true,
    },
    {
      key: "is_seen",
      header: "Visto",
      render: (r) => (r.is_seen ? "Sí" : "No"),
      sortable: true,
    },
    {
      key: "created_at",
      header: "Aplicó",
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "-",
      sortable: true,
    },
  ];

  const getSalary = (o: AdminOffer) => {
    if (parseFloat(o.salary_max || "0") > 0)
      return `${o.salary_min} - ${o.salary_max}`;
    return "No especificado";
  };

  return (
    <section className="panel">
      {/* Header */}
      <div
        style={{
          marginBottom: "24px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {offer.position}
            </h2>
            <span className="text-xs text-slate-400 font-mono select-all">
              ID: {offer.id}
            </span>
          </div>
          <span
            className={`badge ${
              offer.status === "Publish" ? "status-publish" : "status-draft"
            }`}
          >
            {offer.status === "Publish" ? "PUBLICADA" : offer.status}
          </span>
        </div>
        {offer.description && (
          <p className="text-slate-600 text-sm mt-2 italic border-l-2 border-slate-200 pl-3">
            {offer.description}
          </p>
        )}
      </div>

      {/* Details */}
      <div className="details-grid">
        <div>
          <Section title="Información General">
            <DetailRow label="Sector" value={offer.sector} />
            <DetailRow label="Vacantes" value={offer.number_of_vacancies} />
            <DetailRow label="Creada" value={offer.created_at} />
            <DetailRow label="Finaliza" value={offer.end_date} />
          </Section>

          <Section title="Ubicación">
            <DetailRow label="Ubicación Raw" value={offer.location} />
            <DetailRow label="Ciudad" value={offer.city} />
          </Section>
        </div>

        <div>
          <Section title="Condiciones y Salario">
            <DetailRow label="Rango Salarial" value={getSalary(offer)} />
            <DetailRow label="Tipo Trabajo" value={offer.job_type} />
            <DetailRow label="Vehículo Req." value={offer.is_vehicle} isBool />
          </Section>

          <Section title="Configuración">
            <DetailRow label="Código QR" value={offer.qrcode} isBool />
          </Section>
        </div>
      </div>

      <hr className="divider" />

      {/* Candidates Table */}
      <div className="panel-header" style={{ marginTop: "24px" }}>
        <h3 className="panel-title">Candidatos Inscritos</h3>
      </div>
      <span
        className="badge"
        style={{ background: "#f1f5f9", color: "#64748b", fontSize: "12px" }}
      >
        Total: {rows.length}
      </span>
      {loading ? (
        <Empty>Cargando candidatos…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : rows.length ? (
        <Table
          columns={cols}
          rows={sortedRows}
          onRowClick={onSelectCandidate}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      ) : (
        <Empty>Todavía no hay candidatos para esta oferta.</Empty>
      )}
    </section>
  );
}
